import { createContext, useContext, useRef, useState } from "react";
import { PopupModel, Popup } from "./Popup";

export type PopupRecord = PopupModel & {
    hidden: boolean;
    x: number;
    y: number;
    lastWidth: number | null;
    lastHeight: number | null;
    isMaximized: boolean;
    isPinnedLeft: boolean;
    isPinnedRight: boolean;
};

type PopupProviderState = {
    popups: PopupRecord[];
    addPopup: (popup: PopupModel) => void;
    closePopup: (popupId: string) => void;
    focusPopup: (popupId: string) => void;
    hidePopup: (popupId: string) => void;
    openPopup: (popupId: string) => void;
    updatePopup: (popup: { id: string } & Partial<PopupRecord>, shouldRerender?: boolean) => void;
    hideAllPopups: () => void;
};

const initialState: PopupProviderState = {
    popups: [],
    addPopup: () => { },
    closePopup: () => { },
    focusPopup: () => { },
    hidePopup: () => { },
    openPopup: () => { },
    updatePopup: () => { },
    hideAllPopups: () => { },
};

const PopupProviderContext = createContext<PopupProviderState>(initialState);

type PopupProviderProps = {
    children: React.ReactNode;
}

export const getDimension = (value: number | undefined, direction: "x" | "y"): number | undefined => {
    if (!value) return;
    if (isNaN(value)) return;
    if (direction === "x") return value > window.innerWidth ? window.innerWidth * 0.9 : value;
    return value > window.innerHeight ? window.innerHeight * 0.9 : value;
}

export const PopupProvider = ({ children }: PopupProviderProps) => {
    const [_renderTrigger, setRenderTrigger] = useState(0);

    const rerender = () => setRenderTrigger((prev) => prev + 1);

    const popupsRef = useRef<PopupRecord[]>([]);

    const addPopup = (popup: PopupModel) => {
        const popupAlreadyExists = popupsRef.current.some((p) => p.id === popup.id);

        if (popupAlreadyExists) {
            openPopup(popup.id);
            return;
        }

        popupsRef.current = [
            ...popupsRef.current,
            {
                ...popup,
                hidden: false,
                x: 0,
                y: 0,
                lastWidth: null,
                lastHeight: null,
                isMaximized: false,
                isPinnedLeft: false,
                isPinnedRight: false,
                initialWidth: getDimension(popup.initialWidth, "x"),
                initialHeight: getDimension(popup.initialHeight, "y")
            },
        ];

        rerender();
    }

    const closePopup = (popupId: string) => {
        const filteredPopups = popupsRef.current.filter((p) => p.id !== popupId);
        popupsRef.current = filteredPopups;
        rerender();
    }

    const hideAllPopups = () => {
        const hiddenPopups = popupsRef.current.map((p) => ({ ...p, hidden: true }));
        popupsRef.current = hiddenPopups;
        rerender();
    }

    const updatePopup = (data: { id: string; } & Partial<PopupRecord>, shouldRerender: boolean = true) => {
        const index = popupsRef.current.findIndex((p) => p.id === data.id);
        if (index === -1) return;
        const newValue = [
            ...popupsRef.current.slice(0, index),
            { ...popupsRef.current[index], ...data },
            ...popupsRef.current.slice(index + 1),
        ];
        popupsRef.current = newValue;
        if (shouldRerender) rerender();
    }

    const focusPopup = (popupId: string) => {
        if (popupsRef.current[popupsRef.current.length - 1]?.id === popupId) return;
        const foundPopup = popupsRef.current.find((p) => p.id === popupId);
        if (!foundPopup) return;
        popupsRef.current = [
            ...popupsRef.current.filter((p) => p.id !== foundPopup.id),
            {
                ...foundPopup,
                hidden: false,
            },
        ];
        rerender();
    }

    const hidePopup = (popupId: string) => {
        updatePopup({ id: popupId, hidden: true });
    }

    const openPopup = (popupId: string) => {
        updatePopup({ id: popupId, hidden: false });
        focusPopup(popupId);
    }

    const value = {
        popups: popupsRef.current,
        addPopup,
        closePopup,
        focusPopup,
        hidePopup,
        openPopup,
        updatePopup,
        hideAllPopups,
    };

    return (
        <PopupProviderContext.Provider value={value}>
            {children}

            <>
                {popupsRef.current
                    .filter((popup) => !popup.removeFromDomOnMinimize || (popup.removeFromDomOnMinimize && !popup.hidden))
                    .map((popup) => <Popup popup={popup} key={popup.id} />)}
            </>
        </PopupProviderContext.Provider>
    );
}

PopupProvider.usePopups = () => {
    const context = useContext(PopupProviderContext);

    if (!context) {
        throw new Error("usePopups must be used within a PopupProvider");
    }

    return context;
};